import {
  areColinear,
  canonicalizeEdge,
  edgeKey,
  edgesShareEndpoint,
  getVertexPoint,
  NEAR_ZERO_LENGTH_EPSILON,
  polylineIsStraight,
  projectPointOntoInfiniteLine,
} from "./gridMeshGeometry";
import {
  detachChainEndVertices,
  detachEdgeVertices,
  mergeOverlappingChainVertices,
  mergeOverlappingEdgeVertices,
} from "./gridMeshTopology";
import { moveVertex } from "./gridMeshTransform";
import type {
  FrozenEdgeSlideConstraint,
  FrozenVertexSlideConstraint,
  GridEdgeRef,
  GridMesh,
  SlideRail,
  VertexDelta,
} from "./gridMeshTypes";
import {
  distanceSq,
  type BoundaryPoint,
  type MapViewportProjector,
} from "./geo";

/**
 * Max |sin(θ)| between two rail directions to treat them as parallel (or
 * anti-parallel). 0.08 ≈ sin(4.6°) — a feel tolerance for float noise and
 * slightly off-axis meshes when choosing translate-then-project vs oriented slide.
 */
const RAILS_PARALLEL_SIN_EPSILON = 0.08;

/**
 * Straight rail along the plot edge a row divider is constrained to.
 * The divider sits between the two corners, so the rail is that whole edge.
 */
const rowDividerSlideRail = (
  mesh: GridMesh,
  vertexId: string,
): SlideRail | null => {
  const vertex = mesh.vertices[vertexId];
  const edge = vertex?.onEdge;
  if (vertex == null || edge == null) {
    return null;
  }
  const start = getVertexPoint(mesh.vertices, edge.startVertexId);
  const end = getVertexPoint(mesh.vertices, edge.endVertexId);
  if (start == null || end == null) {
    return null;
  }

  return {
    points: [
      start,
      { latitude: vertex.latitude, longitude: vertex.longitude },
      end,
    ],
  };
};

/** Gather unique cell-ring neighbors of a vertex (optionally skipping some), as points. */
const collectRailNeighborPoints = (
  mesh: GridMesh,
  vertexId: string,
  vertex: BoundaryPoint,
  isExcludedNeighbor: (neighborId: string) => boolean,
): BoundaryPoint[] => {
  const neighborIds: string[] = [];
  const seen = new Set<string>();

  for (const cell of mesh.cells) {
    const count = cell.vertexIds.length;
    for (let index = 0; index < count; index += 1) {
      if (cell.vertexIds[index] !== vertexId) {
        continue;
      }

      const prevId = cell.vertexIds[(index - 1 + count) % count];
      const nextId = cell.vertexIds[(index + 1) % count];
      for (const candidate of [prevId, nextId]) {
        if (
          candidate == null ||
          seen.has(candidate) ||
          isExcludedNeighbor(candidate)
        ) {
          continue;
        }
        seen.add(candidate);
        neighborIds.push(candidate);
      }
    }
  }

  return neighborIds
    .map((id) => getVertexPoint(mesh.vertices, id))
    .filter((point): point is BoundaryPoint => point != null)
    .filter((point) => distanceSq(vertex, point) > NEAR_ZERO_LENGTH_EPSILON);
};

/**
 * Build a slide rail polyline from neighbor points:
 * opposite pair through the vertex if possible, else most-perpendicular to the
 * excluded edge, else the first neighbor.
 */
const buildSlideRailFromNeighbors = (
  vertex: BoundaryPoint,
  neighborPoints: BoundaryPoint[],
  excludedEdgeDirection: { x: number; y: number } | null,
): SlideRail | null => {
  if (neighborPoints.length === 0) {
    return null;
  }

  if (neighborPoints.length === 1) {
    return { points: [vertex, neighborPoints[0]] };
  }

  // Prefer the most opposite pair (through-rail). Keep as a polyline so a
  // non-colinear corner retains both segments and their change in direction.
  let bestA: BoundaryPoint | null = null;
  let bestB: BoundaryPoint | null = null;
  let bestScore = Number.NEGATIVE_INFINITY;

  for (let i = 0; i < neighborPoints.length; i += 1) {
    for (let j = i + 1; j < neighborPoints.length; j += 1) {
      const a = neighborPoints[i];
      const b = neighborPoints[j];
      const ax = a.longitude - vertex.longitude;
      const ay = a.latitude - vertex.latitude;
      const bx = b.longitude - vertex.longitude;
      const by = b.latitude - vertex.latitude;
      const aLen = Math.hypot(ax, ay);
      const bLen = Math.hypot(bx, by);
      if (aLen < NEAR_ZERO_LENGTH_EPSILON || bLen < NEAR_ZERO_LENGTH_EPSILON) {
        continue;
      }
      const alignment = (ax * bx + ay * by) / (aLen * bLen);
      const score = -alignment;
      if (score > bestScore) {
        bestScore = score;
        bestA = a;
        bestB = b;
      }
    }
  }

  if (bestA != null && bestB != null && bestScore > 0) {
    return { points: [bestA, vertex, bestB] };
  }

  // Fall back to the neighbor most perpendicular to the excluded edge.
  if (excludedEdgeDirection != null) {
    const edgeLen = Math.hypot(
      excludedEdgeDirection.x,
      excludedEdgeDirection.y,
    );
    let bestPoint: BoundaryPoint | null = null;
    let bestPerpScore = Number.NEGATIVE_INFINITY;
    neighborPoints.forEach((point) => {
      const dx = point.longitude - vertex.longitude;
      const dy = point.latitude - vertex.latitude;
      const len = Math.hypot(dx, dy);
      if (
        len < NEAR_ZERO_LENGTH_EPSILON ||
        edgeLen < NEAR_ZERO_LENGTH_EPSILON
      ) {
        return;
      }
      const absDot = Math.abs(
        (dx * excludedEdgeDirection.x + dy * excludedEdgeDirection.y) /
          (len * edgeLen),
      );
      const score = 1 - absDot;
      if (score > bestPerpScore) {
        bestPerpScore = score;
        bestPoint = point;
      }
    });
    if (bestPoint != null) {
      return { points: [vertex, bestPoint] };
    }
  }

  return { points: [vertex, neighborPoints[0]] };
};

/**
 * Slide rail at a vertex for an edge drag. A row divider uses its stored plot
 * edge. Other vertices use neighbors excluding the edge being slid, falling
 * back to the direction most perpendicular to that edge.
 */
const findSlideRailAtVertex = (
  mesh: GridMesh,
  vertexId: string,
  excludedEdge: GridEdgeRef,
): SlideRail | null => {
  const dividerRail = rowDividerSlideRail(mesh, vertexId);
  if (dividerRail != null) {
    return dividerRail;
  }

  const vertex = getVertexPoint(mesh.vertices, vertexId);
  if (vertex == null) {
    return null;
  }

  const excludedStart = getVertexPoint(
    mesh.vertices,
    excludedEdge.startVertexId,
  );
  const excludedEnd = getVertexPoint(mesh.vertices, excludedEdge.endVertexId);
  const excludedEdgeDirection =
    excludedStart != null && excludedEnd != null
      ? {
          x: excludedEnd.longitude - excludedStart.longitude,
          y: excludedEnd.latitude - excludedStart.latitude,
        }
      : null;

  const neighborPoints = collectRailNeighborPoints(
    mesh,
    vertexId,
    vertex,
    (neighborId) =>
      edgeKey(vertexId, neighborId) ===
      edgeKey(excludedEdge.startVertexId, excludedEdge.endVertexId),
  );

  return buildSlideRailFromNeighbors(
    vertex,
    neighborPoints,
    excludedEdgeDirection,
  );
};

/**
 * Snapshot rails + edge orientation at gesture start so mid-drag mesh edits
 * don't rewrite the constraints the slide should follow.
 */
export const captureEdgeSlideConstraint = (
  mesh: GridMesh,
  edge: GridEdgeRef,
): FrozenEdgeSlideConstraint | null => {
  const start = getVertexPoint(mesh.vertices, edge.startVertexId);
  const end = getVertexPoint(mesh.vertices, edge.endVertexId);
  // Rails exclude the edge being slid so endpoints move along incident edges.
  const startRail = findSlideRailAtVertex(mesh, edge.startVertexId, edge);
  const endRail = findSlideRailAtVertex(mesh, edge.endVertexId, edge);
  if (start == null || end == null || startRail == null || endRail == null) {
    return null;
  }

  return {
    endRail: {
      points: endRail.points.map((point) => ({ ...point })),
    },
    orientation: {
      latitude: end.latitude - start.latitude,
      longitude: end.longitude - start.longitude,
    },
    startRail: {
      points: startRail.points.map((point) => ({ ...point })),
    },
  };
};

/**
 * Snapshot the vertex origin and one rail per connected neighbor edge so the
 * active slide direction can be chosen from frozen geometry during the gesture.
 */
export const captureVertexSlideConstraint = (
  mesh: GridMesh,
  vertexId: string,
): FrozenVertexSlideConstraint | null => {
  const origin = getVertexPoint(mesh.vertices, vertexId);
  if (origin == null) {
    return null;
  }

  // Collect unique adjacent vertex ids from all incident cell rings.
  const neighborIds: string[] = [];
  const seen = new Set<string>();
  for (const cell of mesh.cells) {
    const count = cell.vertexIds.length;
    for (let index = 0; index < count; index += 1) {
      if (cell.vertexIds[index] !== vertexId) {
        continue;
      }
      const prevId = cell.vertexIds[(index - 1 + count) % count];
      const nextId = cell.vertexIds[(index + 1) % count];
      for (const candidate of [prevId, nextId]) {
        if (candidate == null || seen.has(candidate)) {
          continue;
        }
        seen.add(candidate);
        neighborIds.push(candidate);
      }
    }
  }

  // Each neighbor becomes a frozen rail from origin → neighbor.
  const rails = neighborIds.flatMap((neighborVertexId) => {
    const end = getVertexPoint(mesh.vertices, neighborVertexId);
    if (end == null || distanceSq(origin, end) <= NEAR_ZERO_LENGTH_EPSILON) {
      return [];
    }
    return [{ end: { ...end }, neighborVertexId }];
  });

  if (rails.length === 0) {
    return null;
  }

  return {
    origin: { ...origin },
    rails,
  };
};

/** Pick the frozen edge whose line is closest to the current pointer. */
export const pickVertexSlideRailIndex = (
  frozenConstraint: FrozenVertexSlideConstraint,
  pointer: BoundaryPoint,
): number => {
  let bestIndex = 0;
  let bestDistanceSq = Number.POSITIVE_INFINITY;

  frozenConstraint.rails.forEach((rail, index) => {
    const directionLatitude =
      rail.end.latitude - frozenConstraint.origin.latitude;
    const directionLongitude =
      rail.end.longitude - frozenConstraint.origin.longitude;
    const lengthSq = distanceSq(frozenConstraint.origin, rail.end);
    // Skip degenerate rails (origin ≈ end): no direction, and distanceSq / lengthSq
    // would blow up.
    if (lengthSq < NEAR_ZERO_LENGTH_EPSILON) {
      return;
    }

    const toPointerLatitude =
      pointer.latitude - frozenConstraint.origin.latitude;
    const toPointerLongitude =
      pointer.longitude - frozenConstraint.origin.longitude;
    // Perpendicular distance² from pointer to the infinite edge line.
    const cross =
      toPointerLongitude * directionLatitude -
      toPointerLatitude * directionLongitude;
    const distanceSqToLine = (cross * cross) / lengthSq;
    if (distanceSqToLine < bestDistanceSq) {
      bestDistanceSq = distanceSqToLine;
      bestIndex = index;
    }
  });

  return bestIndex;
};

/**
 * Slide a vertex: pick the frozen rail nearest the pointer, project onto that
 * infinite line, then move the vertex there.
 */
export const slideVertex = (
  mesh: GridMesh,
  vertexId: string,
  pointer: BoundaryPoint,
  frozenConstraint: FrozenVertexSlideConstraint | null = null,
): GridMesh => {
  const constraint =
    frozenConstraint ?? captureVertexSlideConstraint(mesh, vertexId);
  if (constraint == null || constraint.rails.length === 0) {
    return mesh;
  }

  // 1) Choose which connected edge the gesture is aiming along.
  const railIndex = pickVertexSlideRailIndex(constraint, pointer);
  const rail = constraint.rails[railIndex];
  if (rail == null) {
    return mesh;
  }

  // Degenerate rail (origin ≈ end): nothing to project onto.
  if (distanceSq(constraint.origin, rail.end) < NEAR_ZERO_LENGTH_EPSILON) {
    return mesh;
  }

  // 2) Project the pointer onto that rail and apply.
  const projected = projectPointOntoInfiniteLine(
    pointer,
    constraint.origin,
    rail.end,
  );

  return moveVertex(mesh, vertexId, projected);
};

/** Unit direction (and length) of a straight rail from first to last point. */
const straightRailDirection = (
  rail: SlideRail,
): { x: number; y: number; length: number } | null => {
  if (!polylineIsStraight(rail.points) || rail.points.length < 2) {
    return null;
  }
  const start = rail.points[0];
  const end = rail.points[rail.points.length - 1];
  const x = end.longitude - start.longitude;
  const y = end.latitude - start.latitude;
  return { length: Math.hypot(x, y), x, y };
};

/** True when both rails are straight and point in nearly the same/opposite directions. */
const areRailsParallel = (
  startRail: SlideRail | null,
  endRail: SlideRail | null,
): boolean => {
  if (startRail == null || endRail == null) {
    return false;
  }

  const a = straightRailDirection(startRail);
  const b = straightRailDirection(endRail);
  if (
    a == null ||
    b == null ||
    a.length < NEAR_ZERO_LENGTH_EPSILON ||
    b.length < NEAR_ZERO_LENGTH_EPSILON
  ) {
    return false;
  }

  // Normalized cross = sin(θ) between rail directions.
  const cross = (a.x * b.y - a.y * b.x) / (a.length * b.length);
  return Math.abs(cross) <= RAILS_PARALLEL_SIN_EPSILON;
};

/**
 * Project onto a rail as an infinite line so outer hull edges can grow past
 * their gesture-start position (clamped segments only allow shrinking inward).
 */
const projectPointOntoRailLine = (
  point: BoundaryPoint,
  rail: SlideRail,
): BoundaryPoint => {
  if (rail.points.length === 0) {
    return point;
  }
  if (rail.points.length === 1) {
    return rail.points[0];
  }

  if (polylineIsStraight(rail.points)) {
    return projectPointOntoInfiniteLine(
      point,
      rail.points[0],
      rail.points[rail.points.length - 1],
    );
  }

  let bestPoint = rail.points[0];
  let bestDistanceSq = Number.POSITIVE_INFINITY;
  for (let index = 0; index < rail.points.length - 1; index += 1) {
    const start = rail.points[index];
    const end = rail.points[index + 1];
    const projected = projectPointOntoInfiniteLine(point, start, end);
    const dist = distanceSq(point, projected);
    if (dist < bestDistanceSq) {
      bestDistanceSq = dist;
      bestPoint = projected;
    }
  }
  return bestPoint;
};

const cross2d = (ax: number, ay: number, bx: number, by: number): number =>
  ax * by - ay * bx;

/**
 * Place `origin` on `originRail` from the gesture, then find the other endpoint on
 * `otherRail` so the edge stays parallel to `orientation` (length may change).
 * Rails are polylines — corners keep both segments (no slope averaging).
 */
const slideOrientedEndpointPair = (
  origin: BoundaryPoint,
  other: BoundaryPoint,
  originRail: SlideRail,
  otherRail: SlideRail,
  delta: VertexDelta,
  orientation: { latitude: number; longitude: number },
): { nextOrigin: BoundaryPoint; nextOther: BoundaryPoint } | null => {
  const proposedOrigin = {
    latitude: origin.latitude + delta.latitude,
    longitude: origin.longitude + delta.longitude,
  };
  const nextOrigin = projectPointOntoRailLine(proposedOrigin, originRail);

  const ux = orientation.longitude;
  const uy = orientation.latitude;
  const orientationLength = Math.hypot(ux, uy);
  if (orientationLength < NEAR_ZERO_LENGTH_EPSILON) {
    return null;
  }

  const originalLength = Math.hypot(
    other.longitude - origin.longitude,
    other.latitude - origin.latitude,
  );

  let bestOther: BoundaryPoint | null = null;
  let bestScore = Number.POSITIVE_INFINITY;

  for (let index = 0; index < otherRail.points.length - 1; index += 1) {
    const segmentStart = otherRail.points[index];
    const segmentEnd = otherRail.points[index + 1];
    const dx = segmentEnd.longitude - segmentStart.longitude;
    const dy = segmentEnd.latitude - segmentStart.latitude;
    // Intersect two infinite lines in 2D:
    //   edge:  nextOrigin + λ * U   (U = frozen edge orientation)
    //   rail:  segmentStart + t * D (D = this rail segment's direction)
    // so nextOrigin + λU = segmentStart + tD
    //    λU - tD = segmentStart - nextOrigin
    // Written as columns [U | -D] * [λ, t]^T = rhs. det = U × (-D); when
    // |det|≈0 the edge is parallel to this rail segment (no unique hit — the
    // ill-conditioned case). We only need λ to place the other endpoint; t is
    // unrestricted so the rail can extend past the segment endpoints.
    const rhsX = segmentStart.longitude - nextOrigin.longitude;
    const rhsY = segmentStart.latitude - nextOrigin.latitude;
    const det = cross2d(ux, uy, -dx, -dy);
    if (Math.abs(det) < NEAR_ZERO_LENGTH_EPSILON) {
      continue;
    }
    const lambda = cross2d(rhsX, rhsY, -dx, -dy) / det;
    const candidate = {
      latitude: nextOrigin.latitude + lambda * uy,
      longitude: nextOrigin.longitude + lambda * ux,
    };
    const lengthDelta = Math.abs(
      Math.hypot(
        candidate.longitude - nextOrigin.longitude,
        candidate.latitude - nextOrigin.latitude,
      ) - originalLength,
    );
    if (lengthDelta < bestScore) {
      bestScore = lengthDelta;
      bestOther = candidate;
    }
  }

  if (bestOther == null) {
    return null;
  }

  return { nextOrigin, nextOther: bestOther };
};

/** Squared distance from `pointer` to the midpoint of segment a→b. */
const midpointDistanceSq = (
  a: BoundaryPoint,
  b: BoundaryPoint,
  pointer: BoundaryPoint,
): number => {
  const mid = {
    latitude: (a.latitude + b.latitude) / 2,
    longitude: (a.longitude + b.longitude) / 2,
  };
  return distanceSq(mid, pointer);
};

/**
 * Slide an edge: translate both endpoints with the pointer, constrained to their
 * rails. Parallel rails keep a simple projection; non-parallel rails preserve
 * the frozen edge orientation while endpoints ride their polylines.
 */
export const slideEdge = (
  mesh: GridMesh,
  edge: GridEdgeRef,
  pointer: BoundaryPoint,
  frozenConstraint: FrozenEdgeSlideConstraint | null = null,
): GridMesh => {
  const start = getVertexPoint(mesh.vertices, edge.startVertexId);
  const end = getVertexPoint(mesh.vertices, edge.endVertexId);
  if (start == null || end == null) {
    return mesh;
  }

  // 1) Gesture delta from the edge midpoint to the pointer.
  const midpoint = {
    latitude: (start.latitude + end.latitude) / 2,
    longitude: (start.longitude + end.longitude) / 2,
  };
  const delta = {
    latitude: pointer.latitude - midpoint.latitude,
    longitude: pointer.longitude - midpoint.longitude,
  };

  const proposedStart = {
    latitude: start.latitude + delta.latitude,
    longitude: start.longitude + delta.longitude,
  };
  const proposedEnd = {
    latitude: end.latitude + delta.latitude,
    longitude: end.longitude + delta.longitude,
  };

  // 2) Prefer gesture-start rails/orientation. Recomputing rails each move lets
  //    the moving endpoints rewrite bent polylines and effectively averages corners.
  const startRail =
    frozenConstraint?.startRail ??
    findSlideRailAtVertex(mesh, edge.startVertexId, edge);
  const endRail =
    frozenConstraint?.endRail ??
    findSlideRailAtVertex(mesh, edge.endVertexId, edge);
  const orientation = frozenConstraint?.orientation ?? {
    latitude: end.latitude - start.latitude,
    longitude: end.longitude - start.longitude,
  };

  let nextStart = proposedStart;
  let nextEnd = proposedEnd;

  if (areRailsParallel(startRail, endRail)) {
    // 3a) Parallel rails: project each endpoint independently onto its rail.
    nextStart = projectPointOntoRailLine(proposedStart, startRail!);
    nextEnd = projectPointOntoRailLine(proposedEnd, endRail!);
  } else if (startRail != null && endRail != null) {
    // 3b) Bent rails: try anchoring from each end and keep the orientation;
    //     pick the candidate whose midpoint stays closest to the pointer.
    const fromStart = slideOrientedEndpointPair(
      start,
      end,
      startRail,
      endRail,
      delta,
      orientation,
    );
    const fromEnd = slideOrientedEndpointPair(
      end,
      start,
      endRail,
      startRail,
      delta,
      {
        latitude: -orientation.latitude,
        longitude: -orientation.longitude,
      },
    );

    if (fromStart != null && fromEnd != null) {
      const startScore = midpointDistanceSq(
        fromStart.nextOrigin,
        fromStart.nextOther,
        pointer,
      );
      const endScore = midpointDistanceSq(
        fromEnd.nextOther,
        fromEnd.nextOrigin,
        pointer,
      );
      if (startScore <= endScore) {
        nextStart = fromStart.nextOrigin;
        nextEnd = fromStart.nextOther;
      } else {
        nextStart = fromEnd.nextOther;
        nextEnd = fromEnd.nextOrigin;
      }
    } else if (fromStart != null) {
      nextStart = fromStart.nextOrigin;
      nextEnd = fromStart.nextOther;
    } else if (fromEnd != null) {
      nextStart = fromEnd.nextOther;
      nextEnd = fromEnd.nextOrigin;
    }
  }

  // 4) Apply both endpoint moves.
  let next = moveVertex(mesh, edge.startVertexId, nextStart);
  next = moveVertex(next, edge.endVertexId, nextEnd);
  return next;
};

/**
 * Single-edge slide: freeze rails from the current (pre-clone) mesh, clone
 * endpoints onto cells that share the edge, then slide those clones.
 */
export const slideSingleEdge = (
  mesh: GridMesh,
  edge: GridEdgeRef,
  pointer: BoundaryPoint,
  frozenConstraint: FrozenEdgeSlideConstraint | null = null,
): { edge: GridEdgeRef; mesh: GridMesh } => {
  const constraint = frozenConstraint ?? captureEdgeSlideConstraint(mesh, edge);
  const detached = detachEdgeVertices(mesh, edge);
  return {
    edge: detached.edge,
    mesh: slideEdge(detached.mesh, detached.edge, pointer, constraint),
  };
};

/**
 * After a single-edge slide gesture: snap the edge onto any overlapping
 * foreign vertex (keeping slope) and merge the coincident ids.
 */
export const finalizeSingleEdgeSlide = (
  mesh: GridMesh,
  edge: GridEdgeRef,
): { edge: GridEdgeRef; mesh: GridMesh } =>
  mergeOverlappingEdgeVertices(mesh, edge);

/**
 * Chain slide: freeze rails from the current (pre-clone) mesh, clone only the
 * terminal vertices onto cells that own the end edges, then slide the chain.
 */
export const slideDetachedEdgeChain = (
  mesh: GridMesh,
  vertexIds: string[],
  pointer: BoundaryPoint,
  seedEdge: GridEdgeRef,
  frozenConstraint: FrozenEdgeSlideConstraint | null = null,
): { edge: GridEdgeRef; mesh: GridMesh; vertexIds: string[] } => {
  const constraint =
    frozenConstraint ?? captureEdgeSlideConstraint(mesh, seedEdge);
  const detached = detachChainEndVertices(mesh, vertexIds);
  const remappedByOriginal = new Map(
    vertexIds.map((vertexId, index) => [
      vertexId,
      detached.vertexIds[index] ?? vertexId,
    ]),
  );
  const remappedEdge = {
    endVertexId:
      remappedByOriginal.get(seedEdge.endVertexId) ?? seedEdge.endVertexId,
    startVertexId:
      remappedByOriginal.get(seedEdge.startVertexId) ?? seedEdge.startVertexId,
  };
  return {
    edge: remappedEdge,
    mesh: slideEdgeChain(
      detached.mesh,
      detached.vertexIds,
      pointer,
      remappedEdge,
      constraint,
    ),
    vertexIds: detached.vertexIds,
  };
};

/**
 * After a chain slide gesture: snap the wall onto any overlapping foreign
 * vertex (keeping slope) and merge the coincident terminal ids.
 */
export const finalizeEdgeChainSlide = (
  mesh: GridMesh,
  vertexIds: string[],
): { mesh: GridMesh; vertexIds: string[] } =>
  mergeOverlappingChainVertices(mesh, vertexIds);

/**
 * Find vertices along a colinear edge run through the selection starting from
 * seedEdge (used so move/slide can treat a straight wall as one chain).
 */
export const findContinuousEdgeChain = (
  mesh: GridMesh,
  selectedIds: string[],
  seedEdge: GridEdgeRef,
): string[] => {
  const selected = new Set(selectedIds);
  const selectedCells = mesh.cells.filter((cell) => selected.has(cell.id));
  if (selectedCells.length === 0) {
    return [seedEdge.startVertexId, seedEdge.endVertexId];
  }

  const seedStart = getVertexPoint(mesh.vertices, seedEdge.startVertexId);
  const seedEnd = getVertexPoint(mesh.vertices, seedEdge.endVertexId);
  if (seedStart == null || seedEnd == null) {
    return [seedEdge.startVertexId, seedEdge.endVertexId];
  }

  // 1) Collect selected-cell edges that lie on the same supporting line as the seed.
  const candidateEdges: GridEdgeRef[] = [];
  selectedCells.forEach((cell) => {
    for (let index = 0; index < cell.vertexIds.length; index += 1) {
      const startVertexId = cell.vertexIds[index];
      const endVertexId = cell.vertexIds[(index + 1) % cell.vertexIds.length];
      if (startVertexId == null || endVertexId == null) {
        continue;
      }
      const start = getVertexPoint(mesh.vertices, startVertexId);
      const end = getVertexPoint(mesh.vertices, endVertexId);
      if (start == null || end == null) {
        continue;
      }
      if (!areColinear(seedStart, seedEnd, start, end)) {
        continue;
      }
      candidateEdges.push(canonicalizeEdge(startVertexId, endVertexId));
    }
  });

  // 2) Dedupe undirected edges.
  const unique = new Map<string, GridEdgeRef>();
  candidateEdges.forEach((edge) => {
    unique.set(edgeKey(edge.startVertexId, edge.endVertexId), edge);
  });
  const edges = [...unique.values()];
  if (edges.length === 0) {
    return [seedEdge.startVertexId, seedEdge.endVertexId];
  }

  const seedKey = edgeKey(seedEdge.startVertexId, seedEdge.endVertexId);
  if (!unique.has(seedKey)) {
    edges.push(canonicalizeEdge(seedEdge.startVertexId, seedEdge.endVertexId));
  }

  // 3) BFS through edges that share an endpoint → connected colinear component.
  const visited = new Set<string>();
  const queue = [seedKey];
  const component: GridEdgeRef[] = [];

  while (queue.length > 0) {
    const key = queue.shift();
    if (key == null || visited.has(key)) {
      continue;
    }
    visited.add(key);
    const edge =
      unique.get(key) ??
      (key === seedKey
        ? canonicalizeEdge(seedEdge.startVertexId, seedEdge.endVertexId)
        : null);
    if (edge == null) {
      continue;
    }
    component.push(edge);
    edges.forEach((other) => {
      const otherKey = edgeKey(other.startVertexId, other.endVertexId);
      if (!visited.has(otherKey) && edgesShareEndpoint(edge, other)) {
        queue.push(otherKey);
      }
    });
  }

  if (component.length <= 1) {
    return [seedEdge.startVertexId, seedEdge.endVertexId];
  }

  // 4) Return the unique vertex ids in that component (unordered set).
  const vertexIds = new Set<string>();
  component.forEach((edge) => {
    vertexIds.add(edge.startVertexId);
    vertexIds.add(edge.endVertexId);
  });

  return [...vertexIds];
};

/**
 * Slide rail at a chain endpoint. A row divider uses its stored plot edge.
 * Other vertices use the neighbor rail, skipping other chain vertices so the
 * wall slides against the rest of the mesh.
 */
const findSlideRailAtVertexExcludingNeighbors = (
  mesh: GridMesh,
  vertexId: string,
  excludedNeighborIds: Set<string>,
): SlideRail | null => {
  const dividerRail = rowDividerSlideRail(mesh, vertexId);
  if (dividerRail != null) {
    return dividerRail;
  }

  const vertex = getVertexPoint(mesh.vertices, vertexId);
  if (vertex == null) {
    return null;
  }

  const neighborPoints = collectRailNeighborPoints(
    mesh,
    vertexId,
    vertex,
    (neighborId) => excludedNeighborIds.has(neighborId),
  );

  return buildSlideRailFromNeighbors(vertex, neighborPoints, null);
};

/**
 * Slide a whole colinear edge chain with the pointer. Rails ignore other chain
 * vertices so the wall slides against the rest of the mesh, not along itself.
 */
export const slideEdgeChain = (
  mesh: GridMesh,
  vertexIds: string[],
  pointer: BoundaryPoint,
  seedEdge: GridEdgeRef,
  frozenConstraint: FrozenEdgeSlideConstraint | null = null,
): GridMesh => {
  const start = getVertexPoint(mesh.vertices, seedEdge.startVertexId);
  const end = getVertexPoint(mesh.vertices, seedEdge.endVertexId);
  if (start == null || end == null) {
    return mesh;
  }

  // Gesture delta from the seed edge midpoint.
  const midpoint = {
    latitude: (start.latitude + end.latitude) / 2,
    longitude: (start.longitude + end.longitude) / 2,
  };
  const delta = {
    latitude: pointer.latitude - midpoint.latitude,
    longitude: pointer.longitude - midpoint.longitude,
  };

  // Rails exclude other chain vertices so motion is relative to outside neighbors.
  const chainSet = new Set(vertexIds);
  const startRail = findSlideRailAtVertexExcludingNeighbors(
    mesh,
    seedEdge.startVertexId,
    chainSet,
  );
  const endRail = findSlideRailAtVertexExcludingNeighbors(
    mesh,
    seedEdge.endVertexId,
    chainSet,
  );
  const railsAreParallel = areRailsParallel(startRail, endRail);

  if (!railsAreParallel) {
    // Bent case: slide the seed with orientation preserved, then apply the
    // average endpoint translation to the rest of the chain (rail-projected).
    const slid = slideEdge(mesh, seedEdge, pointer, frozenConstraint);
    const beforeStart = mesh.vertices[seedEdge.startVertexId];
    const beforeEnd = mesh.vertices[seedEdge.endVertexId];
    const afterStart = slid.vertices[seedEdge.startVertexId];
    const afterEnd = slid.vertices[seedEdge.endVertexId];
    if (
      beforeStart == null ||
      beforeEnd == null ||
      afterStart == null ||
      afterEnd == null
    ) {
      return slid;
    }

    const averageDelta = {
      latitude:
        (afterStart.latitude -
          beforeStart.latitude +
          (afterEnd.latitude - beforeEnd.latitude)) /
        2,
      longitude:
        (afterStart.longitude -
          beforeStart.longitude +
          (afterEnd.longitude - beforeEnd.longitude)) /
        2,
    };

    let next = slid;
    vertexIds.forEach((vertexId) => {
      if (
        vertexId === seedEdge.startVertexId ||
        vertexId === seedEdge.endVertexId
      ) {
        return;
      }
      const vertex = next.vertices[vertexId];
      if (vertex == null) {
        return;
      }
      const proposed = {
        latitude: vertex.latitude + averageDelta.latitude,
        longitude: vertex.longitude + averageDelta.longitude,
      };
      const rail = findSlideRailAtVertexExcludingNeighbors(
        next,
        vertexId,
        chainSet,
      );
      next = moveVertex(
        next,
        vertexId,
        rail == null ? proposed : projectPointOntoRailLine(proposed, rail),
      );
    });
    return next;
  }

  // Parallel case: translate every chain vertex by the same delta, then project
  // each onto its own outside rail.
  let next = mesh;
  vertexIds.forEach((vertexId) => {
    const vertex = next.vertices[vertexId];
    if (vertex == null) {
      return;
    }
    const proposed = {
      latitude: vertex.latitude + delta.latitude,
      longitude: vertex.longitude + delta.longitude,
    };

    const rail = findSlideRailAtVertexExcludingNeighbors(
      next,
      vertexId,
      chainSet,
    );
    const constrained =
      rail == null ? proposed : projectPointOntoRailLine(proposed, rail);
    next = moveVertex(next, vertexId, constrained);
  });

  return next;
};

/**
 * Screen-space bearing of an edge in degrees (atan2 of projected end−start),
 * used to pick resize cursors that match edge orientation.
 */
export const edgeScreenAngleDegrees = (
  map: MapViewportProjector,
  mesh: GridMesh,
  edge: GridEdgeRef,
): number => {
  const start = getVertexPoint(mesh.vertices, edge.startVertexId);
  const end = getVertexPoint(mesh.vertices, edge.endVertexId);
  if (start == null || end == null) {
    return 0;
  }

  const startPx = map.project([start.longitude, start.latitude]);
  const endPx = map.project([end.longitude, end.latitude]);
  return (Math.atan2(endPx.y - startPx.y, endPx.x - startPx.x) * 180) / Math.PI;
};
